import React from "react";
import { FullscreenModal } from "@learningblocks/storybook-components";
import { isActivationKey } from "@learningblocks/shared-components";

import { Tooltip, TooltipProps } from "./Tooltip";
import "./ProtocolEquipmentModal.scss";

// Generic icon imports
import Edit from "./images/edit.svg";
import Hazard from "./images/hazard-black.svg";
import Error from "./images/error.svg";
import Success from "./images/success.svg";

/**
 * Configuration for a single table cell
 */
export interface TableCellConfig {
  value: string | number;
  showRecordButton?: boolean;
  recordButtonDisabled?: boolean;
  recordButtonId?: string; // Identifier for this record button (passed to callback)
  ariaLabel?: string;
}

/**
 * Configuration for a table row
 */
export interface TableRowConfig {
  id: string; // Unique identifier for this row
  cells: TableCellConfig[];
}

/**
 * Configuration for the data recording table
 */
export interface TableConfig {
  headers: string[];
  rows: TableRowConfig[];
  ariaLabel?: string;
}

/**
 * Configuration for the footer warning/status section
 */
export interface FooterConfig {
  heading: string;
  content: string;
}

/**
 * Current warning/status to display in footer
 */
export interface CurrentWarning {
  message: string;
  type: "success" | "error";
}

/**
 * Configuration for a clickable button area on the equipment image
 */
export interface EquipmentButtonConfig {
  id: string;
  position: {
    top?: string;
    left?: string;
    right?: string;
    bottom?: string;
    width: string;
    height: string;
  };
  onClick: () => void;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Props for the ProtocolEquipmentModal component
 */
export interface ProtocolEquipmentModalProps {
  // Modal control
  show: boolean;
  close: () => void;

  // Equipment display
  equipmentImage: string; // App provides the complete equipment image (with items on it)
  value?: number; // Current value to display (e.g., weight, temperature) - optional

  // Table configuration (optional)
  tableConfig?: TableConfig; // Complete table structure provided by app

  // Tooltip
  tooltip: TooltipProps;

  // Callbacks - app handles all business logic (optional)
  onRecordButtonClick?: (
    rowId: string,
    cellId: string,
    target: HTMLElement
  ) => void;
  onZeroButtonClick?: () => void;

  // Content customization
  title?: string;
  description?: string;
  footerConfig?: FooterConfig;
  currentWarning?: CurrentWarning;

  // Custom styling
  className?: string; // Additional class name added alongside "modal" for style overrides

  // Layout
  singleColumnView?: boolean; // Hide left column and center the equipment

  // Custom content
  leftColumnContent?: React.ReactNode; // Custom content for left column (replaces table/footer)
  renderRightColumn?: () => React.ReactNode; // Custom right column renderer

  // Value display customization
  valueFormatter?: (value: number) => string; // Custom formatter for value display (default: "{value.toFixed(2)}g")
  valuePosition?: {
    top?: string;
    left?: string;
    right?: string;
    bottom?: string;
  }; // Custom position for value display

  // Equipment buttons (clickable areas on the equipment image)
  equipmentButtons?: EquipmentButtonConfig[];
}

export class ProtocolEquipmentModal extends React.Component<ProtocolEquipmentModalProps> {
  static defaultProps = {
    title: "Record measurements",
    description: "Record measurements by clicking the 'Record' button",
    tableConfig: {
      headers: [],
      rows: [],
    },
  };

  /**
   * Render a record button for a table cell
   */
  private renderRecordButton = (
    rowId: string,
    cellId: string,
    disabled = false
  ) => {
    const { onRecordButtonClick } = this.props;

    return (
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        className={`record-button ${disabled ? "disabled" : ""}`}
        aria-disabled={disabled}
        onKeyDown={(e) => {
          if (!disabled && isActivationKey(e) && onRecordButtonClick) {
            onRecordButtonClick(rowId, cellId, e.currentTarget);
          }
        }}
        onClick={(e) => {
          if (!disabled && onRecordButtonClick) {
            onRecordButtonClick(rowId, cellId, e.currentTarget);
          }
        }}
      >
        <img width="14px" height="14px" src={Edit} alt="Edit" />
        <div className="record-button-text">Record</div>
      </div>
    );
  };

  /**
   * Render the data recording table
   */
  private renderTable = () => {
    const { tableConfig } = this.props;

    if (!tableConfig || tableConfig.rows.length === 0) {
      return null;
    }

    return (
      <div className="table-container">
        <table aria-label={tableConfig.ariaLabel}>
          <thead>
            <tr>
              {tableConfig.headers.map((header, index) => (
                <th key={`header-${index}`}>{header}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableConfig.rows.map((row) => (
              <tr key={row.id}>
                {row.cells.map((cell, cellIndex) => (
                  <td
                    key={`${row.id}-cell-${cellIndex}`}
                    aria-label={cell.ariaLabel}
                    tabIndex={cell.ariaLabel ? 0 : undefined}
                  >
                    <div className="cell-content">
                      <span className="cell-value">{cell.value}</span>
                      {cell.showRecordButton &&
                        this.renderRecordButton(
                          row.id,
                          cell.recordButtonId || `${row.id}-${cellIndex}`,
                          cell.recordButtonDisabled
                        )}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  /**
   * Render the footer section with warnings/status
   */
  private renderFooter = () => {
    const { footerConfig, currentWarning } = this.props;

    if (!footerConfig) {
      return null;
    }

    return (
      <div className="footer">
        <div className="footerHeading">
          <img
            style={{
              width: "17.50px",
              height: "15.96px",
              paddingRight: "4px",
              position: "relative",
              top: "1px",
            }}
            src={Hazard}
            alt=""
          />
          {footerConfig.heading}
        </div>
        <div className="footerContent">{footerConfig.content}</div>
        {currentWarning && (
          <div
            className={`footerWarning ${currentWarning.type === "success" ? "success" : ""}`}
          >
            <span>{currentWarning.message}</span>
            <img
              style={{
                width: "19.381px",
                height: "22px",
                paddingRight: "4px",
                position: "relative",
                top: "6px",
              }}
              src={currentWarning.type === "success" ? Success : Error}
              alt=""
            />
          </div>
        )}
      </div>
    );
  };

  renderEquipmentButtons = () => {
    const { equipmentButtons, onZeroButtonClick } = this.props;

    // If equipmentButtons are provided, render them
    if (equipmentButtons && equipmentButtons.length > 0) {
      return equipmentButtons.map((button) => (
        <button
          key={button.id}
          className={`equipment-button ${button.className || ""}`}
          style={{
            position: "absolute",
            ...button.position,
            background: "transparent",
            border: "none",
            cursor: button.disabled ? "not-allowed" : "pointer",
            opacity: button.disabled ? 0.5 : 1,
          }}
          onClick={button.disabled ? undefined : button.onClick}
          aria-label={button.ariaLabel}
          disabled={button.disabled}
        />
      ));
    }

    // Fall back to legacy zeroButton if onZeroButtonClick is provided
    if (onZeroButtonClick) {
      return (
        <button
          className="zeroButton"
          onClick={onZeroButtonClick}
          aria-label="Zero scale"
        />
      );
    }

    return null;
  };

  renderDefaultRightColumn = () => {
    const { equipmentImage, value, valueFormatter, valuePosition } = this.props;

    const defaultFormatter = (v: number) => `${v.toFixed(2)} g`;
    const formatValue = valueFormatter || defaultFormatter;

    const textContainerStyle = valuePosition
      ? {
          position: "absolute" as const,
          ...valuePosition,
        }
      : undefined;

    return (
      <div className="scale">
        <img src={equipmentImage} alt="Equipment" />
        {this.renderEquipmentButtons()}
        <div className="text-container" style={textContainerStyle}>
          <div className="text">
            {value !== undefined ? formatValue(value) : ""}
          </div>
        </div>
      </div>
    );
  };

  render() {
    const {
      show,
      close,
      tooltip,
      title,
      description,
      leftColumnContent,
      renderRightColumn,
      className,
      singleColumnView,
    } = this.props;

    const modalClassName = className ? `modal ${className}` : "modal";

    return (
      <FullscreenModal
        className="modalContainer"
        isOpen={show}
        closeButton={true}
        notebookExpanded={false}
        close={close}
        backLabel="Lab"
      >
        <div
          className={modalClassName}
          style={{ display: show ? "block" : "none" }}
        >
          <Tooltip {...tooltip} />
          <div
            className={`container ${singleColumnView ? "single-column" : ""}`}
          >
            {!singleColumnView && (
              <div className="left-column">
                <div className="content">
                  <h1>{title}</h1>
                  <p>{description}</p>
                  {leftColumnContent || (
                    <>
                      {this.renderTable()}
                      {this.renderFooter()}
                    </>
                  )}
                </div>
              </div>
            )}
            <div className="right-column">
              {renderRightColumn
                ? renderRightColumn()
                : this.renderDefaultRightColumn()}
            </div>
          </div>
        </div>
      </FullscreenModal>
    );
  }
}
export { Tooltip, type TooltipProps } from "./Tooltip";
